package dev.lingvo.site.pages.review

import dev.lingvo.shared.ScreenshotPublicReviewDto
import dev.lingvo.site.api.ApiConfig
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.TimeoutCancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.withTimeout
import kotlinx.serialization.json.Json
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response
import org.w3c.files.Blob

/** Public capability requests never read a session, use cookies, or include the secret in a URL or error. */
internal object PublicReviewApi {
    private val json = Json { ignoreUnknownKeys = true }

    suspend fun review(token: String): ScreenshotPublicReviewDto = read(token, "/screenshot-reviews") { response ->
        val raw = response.text().await()
        if (raw.length > 2_097_152) throw PublicReviewUnavailable()
        val result = json.decodeFromString<ScreenshotPublicReviewDto>(raw)
        if (result.images.isEmpty() || result.images.size > 60 || result.images.map { it.id }.distinct().size != result.images.size || result.images.any {
                it.width !in 1..8192 || it.height !in 1..8192 || !Regex("^[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}$").matches(it.id)
            }) throw PublicReviewUnavailable()
        result
    }

    suspend fun image(token: String, id: String): Blob = read(token, "/screenshot-reviews/images/${encodeURIComponent(id)}") { response ->
        val type = response.headers.get("Content-Type")?.substringBefore(';')
        if (type !in setOf("image/png", "image/jpeg")) throw PublicReviewUnavailable()
        val blob = response.blob().await()
        if (blob.size.toDouble() > 8 * 1024 * 1024) throw PublicReviewUnavailable()
        blob
    }

    private suspend fun <T> read(token: String, path: String, decode: suspend (Response) -> T): T {
        if (publicReviewToken(token) == null) throw PublicReviewUnavailable()
        val controller: dynamic = js("typeof AbortController === 'undefined' ? null : new AbortController()")
        try {
            return withTimeout(20_000) {
                val init = RequestInit(method = "GET", headers = Headers().apply { set("Authorization", "Bearer $token") })
                init.asDynamic().credentials = "omit"
                init.asDynamic().referrerPolicy = "no-referrer"
                init.asDynamic().cache = "no-store"
                init.asDynamic().redirect = "error"
                if (controller != null) init.asDynamic().signal = controller.signal
                val response = window.fetch("${ApiConfig.baseUrl}$path", init).await()
                if (!response.ok) throw PublicReviewUnavailable()
                decode(response)
            }
        } catch (_: TimeoutCancellationException) { throw PublicReviewUnavailable() }
        catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { throw PublicReviewUnavailable() }
        finally { controller?.abort() }
    }
}

/** One opaque failure prevents expired, revoked, invalid or inaccessible links from becoming an oracle. */
internal class PublicReviewUnavailable : RuntimeException("Review unavailable")
private external fun encodeURIComponent(value: String): String
